import { AfterViewInit, ApplicationRef, Component, ComponentFactoryResolver, Injector, Input, OnDestroy, Type, ViewEncapsulation } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AuthHelperService } from 'src/app/auth-helper.service';
import { BlockConfigService } from "../../block-config.service";
import { BlogService } from 'src/app/blog.service';
import { LocalisationService } from 'src/app/localisation.service';
import { SportDataService } from 'src/app/sport-data.service';
import { BetService } from 'src/app/bet.service';
import { MatchListBlockComponent } from 'src/app/fixtures/match-list-block/match-list-block.component';
import { InContentBlockShellComponent } from '../in-content-block-shell/in-content-block-shell.component';
import { FuturesMiniBlockComponent } from 'src/app/sidebar/futures-mini-block/futures-mini-block.component';
import { PreMatchPredictionsBlockComponent } from '../pre-match-predictions-block/pre-match-predictions-block.component';
import { MatchPageHeaderBlockComponent } from '../match-page-header-block/match-page-header-block.component';
import { MatchBestBetBlockComponent } from '../match-best-bet-block/match-best-bet-block.component';
import { BettingActionBannerComponent } from '../betting-action-banner/betting-action-banner.component';
import { ContentBannerComponent } from '../content-banner/content-banner.component';
import { environment } from 'src/environments/environment';
import { ElementRef } from '@angular/core';
import { SwcPreviewBannerComponent } from 'src/app/general/swc-preview-banner/swc-preview-banner.component';
import { SubscribePromptComponent } from '../subscribe-prompt/subscribe-prompt.component';
import { AiPromptComponent } from '../ai-prompt/ai-prompt.component';
import { DisplayAdComponent } from 'src/app/general/display-ad/display-ad.component';

@Component({
  selector: 'app-article-body',
  templateUrl: './article-body.component.html',
  styleUrls: ['./article-body.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ArticleBodyComponent implements AfterViewInit, OnDestroy {
  @Input() content: SafeHtml;
  @Input() articleID: number;

  inContentBlocks: Array<InContentBlockShellComponent> = [];

  environment = environment;

  constructor(
    private authHelper: AuthHelperService,
    private blockConfig: BlockConfigService,
    private betService: BetService,
    private blogService: BlogService,
    private localisationService: LocalisationService,
    private sportDataService: SportDataService,
    private appRef: ApplicationRef,
    private componentFactoryResolver: ComponentFactoryResolver,
    private injector: Injector,
    private sanitiser: DomSanitizer,
    private el: ElementRef,
  ) { }

  ngAfterViewInit(): void {

    if (typeof window !== "undefined" && typeof document !== "undefined") {

      if (environment.auth0Domain) {
        const thisElement = document.getElementsByTagName("app-article-body")[0];
        Array.from(thisElement.querySelectorAll(".registerlink")).forEach(e => {
          e.removeAttribute("href");
          e.addEventListener("click", () => {
            this.authHelper.goToLoginWithState(true);
          })
        })
      }
      

      // external script from Metabet that causes team names to turn into betting links

      if (environment.optionalFeatures.includes("metabet-bubbles")) {
        if (Array.from(document.scripts).some(s => s.src === `https://go.metabet.io/js/global.js?siteID=dimers`)) {
          const oldScript = Array.from(document.scripts).find(s => s.src === `https://go.metabet.io/js/global.js?siteID=dimers`);
          oldScript.parentNode.removeChild(oldScript);
        }
        
        let js, fjs = document.getElementsByTagName("script")[0];
        js = document.createElement("script");
        js.src = `https://go.metabet.io/js/global.js?siteID=dimers`;
        js.defer = true;
        js.onload = () => {
          // script is supposed to automatically run, but relies on DOMContentLoaded not having already fired, so inner method
          // must be called directly
          window["mb_DOMContentLoaded"]()
        }
        fjs.parentNode.insertBefore(js, fjs);
      }

      // external script from Twitter to populate embedded tweets
      if (!(<any>window).twttr?.widgets) {
        (<any>window).twttr = (function(d, s, id) {
          let js, fjs = d.getElementsByTagName(s)[0], t = (<any>window).twttr || {};
  
          if (d.getElementById(id)) return t;
          js = d.createElement(s);
          js.id = id;
          js.src = 'https://platform.twitter.com/widgets.js';
          fjs.parentNode.insertBefore(js, fjs);
          t._e = [];
          t.ready = function(f) {
            t._e.push(f);
            window["twttr"].widgets.load();
          };
          return t;
        }(document, 'script', 'twitter-wjs'));
      } else {
        (<any>window).twttr.widgets.load();
      }
      
    }

    // Convert old SI in-content marker format to new one

    const preMatchBlockTargets = this.el.nativeElement.innerHTML.match(/\{# PRE-MATCH (\S+) #\}/g);
    if (preMatchBlockTargets && preMatchBlockTargets.length > 0) {
      preMatchBlockTargets.forEach((instance) => {
        const matchID = instance.match(/\{# PRE-MATCH (\S+) #\}/)[1];
        this.el.nativeElement.innerHTML = this.el.nativeElement.innerHTML.replace(/\{# PRE-MATCH (\S+) #\}/, `<div data-widget="external-pre-match-predictions" data-widget-append="{&quot;matchID&quot;: &quot;${matchID}&quot;}"></div>`);
      })

    }

    if (typeof window !== "undefined" && typeof document !== "undefined") {
      /**
       * Group by widget type
       */

      const widgetAttrs: Array<string> = [...new Set(Array.from(document.querySelectorAll("[data-widget]")).map(e => e.getAttribute("data-widget")))];
      const widgets: Record<string, Array<HTMLElement>> = widgetAttrs.reduce((acc, cur) => ({
        ...acc,
        [cur]: Array.from(document.querySelectorAll("[data-widget]")).filter(e => e.getAttribute("data-widget") === cur)
      }), {})

      /**
       * We will mapped the default configuration and function
       * so that we don't have to do some conditional statements
       * */
      const defaultWidgets = {
        'external-match-list': {
          config: {
            ...this.blockConfig.getGlobalBlockConfig(),
          },
          function: (config) => this.loadNativeContentBlock(config, MatchListBlockComponent),
        },
        'external-pre-match-predictions': {
          config: {
            ...this.blockConfig.getGlobalBlockConfig(),
          },
          function: (config) => this.loadNativeContentBlock(config, PreMatchPredictionsBlockComponent),
        },
        'external-match-page-header': {
          config: {
            ...this.blockConfig.getGlobalBlockConfig(),
          },
          function: (config) => this.loadNativeContentBlock(config, MatchPageHeaderBlockComponent),
        },
        'external-match-best-bet': {
          config: {
            ...this.blockConfig.getGlobalBlockConfig(),
          },
          function: (config) => this.loadNativeContentBlock(config, MatchBestBetBlockComponent),
        },
        'external-futures-mini': {
          config: {
            ...this.blockConfig.getGlobalBlockConfig(),
          },
          function: (config) => this.loadNativeContentBlock(config, FuturesMiniBlockComponent),
        },
        'subscribe-prompt': {
          config: {},
          function: (config) => this.loadNativeContentBlock(config, SubscribePromptComponent)
        },
        'ai-prompt': {
          config: {},
          function: (config) => this.loadNativeContentBlock(config, AiPromptComponent)
        },
        'swc-preview': {
          config: {},
          function: (config) => this.loadWorldCupPreviewBlock(config),
        },
        'in-article-promo': {
          config: { promo: 'in-article' },
          function: (config) => this.loadPromo(config),
        },
        'in-content-banner': {
          function: (config) => this.loadContentBanner(config),
        },
        'betting-action-banner': {
          function: (config) => this.loadBettingActionBanner(config),
        },
        slug: {
          config: { promo: 'slug' },
          function: (config) => this.loadPromo(config),
        },
        'sb101-poll': {
          config: {},
          function: (config) => this.loadPolls(config),
        },
        'in-article-ad': {
          config: {},
          function: (config) => this.loadNativeContentBlock(config, DisplayAdComponent)
        }
      }

      Object.keys(widgets).forEach((key) => {
        /**
         * Get all the configuration and add a unique id on each widget
         */
        const configs = widgets[key].map((widget) => {
          const id = `si-${widget.getAttribute('data-widget')}-widget-${this.blogService.getIncrement()}`
          const widgetType = widget.getAttribute('data-widget')
          let config: Record<string, any> = {}
  
          // Get /go slug and append to hyperlinkId
          config =
            widgetType === 'slug'
              ? {
                  hyperlinkId: widget.getAttribute('href').includes('?')
                    ? widget
                        .getAttribute('href')
                        .substring(
                          widget.getAttribute('href').lastIndexOf('go/') + 3,
                          widget.getAttribute('href').indexOf('?')
                        )
                    : widget.getAttribute('href').match(/go\/(.*)/)[1],
                  customStyle: widget.getAttribute('style'),
                  innerHTML: widget.innerHTML,
                }
              : JSON.parse(widget.getAttribute('data-widget-append') || "{}")
  
          config.targetElementID = id
          widget.setAttribute('id', id)
          widget.classList.add('hypometer-widget')
  
          /**
           * Map the default configurations.
           *
           * Let's do some cloning since merge mutate objects and we don't want that.
           */
          return {
            ...defaultWidgets[key].config,
            ...config,
          }
        })
  
        /**
         * Call the function to load the configurations
         */
        if (Object.keys(defaultWidgets).includes(key) && defaultWidgets[key].function) {
          defaultWidgets[key].function(configs)
        } else {
          console.log(`No registered function for: ${key}`)
        }
      })

      // check for Chalkline Games elements
      const chalklineWidgets = document.querySelectorAll("[data-chalkline-link]");
      if (chalklineWidgets.length > 0) {
        // external script that populates Chalkline Games elements
              
        if (Array.from(document.scripts).some(s => s.src.includes(`https://games.dimers.com/integration/embed.js`))) {
          const oldScript = Array.from(document.scripts).find(s => s.src.includes(`https://games.dimers.com/integration/embed.js`));
          oldScript.parentNode.removeChild(oldScript);
        }

        let js, fjs = document.getElementsByTagName("script")[0];
        js = document.createElement("script");
        js.src = `https://games.dimers.com/integration/embed.js?ts=${Date.now()}`;
        js.defer = true;
        js.onload = () => {

          // iterate through Chalkline elements and activate them using script
          Array.from(chalklineWidgets).forEach((widget, index) => {
            const chalklineLink = widget.getAttribute("data-chalkline-link");
    
            // if widget doesn't have an ID attribute, populate a unique one
            if (!widget.getAttribute("id")) {
              widget.setAttribute("id", `chalkline_iframe_container_${index}`);
            }
    
            const clientConfig = {
              server: chalklineLink,
              target: widget.getAttribute("id"),
              token: null,
              configuration: {
                isHeaderEnabled: false,
              }
            };
    
            window["ChalklineGames"].bootstrap(clientConfig);
          })
        }
        fjs.parentNode.insertBefore(js, fjs);
      }

        
  
    }
  }
  loadPromo(config) {
    if (typeof document !== undefined) {
      for (let i = 0; i < config.length; i++) {
        this.blogService.getHyperlink(config[i].hyperlinkId)
          .subscribe(res => {
  
            const link = document.createElement("a");
            link.setAttribute("ref", "slugUrl");
            link.setAttribute("style", config[i].customStyle || "");

            let url = `/go/${res.go_url_slug}?&src=in_article&affiliateLink=${encodeURIComponent(res.affiliate_hyperlink)}`;
            const bookie = res.hyperlinked_url;
            const type = environment.auth0Domain ? (this.authHelper.user?.id || 'visitor') : "visitor";

            const cUrl = `${this.articleID}|${type}`

            link.setAttribute("href", this.excludeBookies(bookie?.name || "")
              ? url
              : url.includes('&c=')
              ? url.replace(`&c=`, `&c=${cUrl}`)
              : `${url}?c=${cUrl}`);

            link.setAttribute("target", "_blank");
            link.innerHTML = config[i].innerHTML;

            link.addEventListener("click", (e) => {
              e.preventDefault();
              url = this.excludeBookies(bookie?.name || "")
                ? url
                : url.includes('&c=')
                ? url.replace(`&c=`, `&c=${cUrl}`)
                : `${url}?c=${cUrl}`

              window.open(`${url}`, '_blank')
            })

            const targetElement = document.getElementById(config[i].targetElementID);
            targetElement.parentNode.replaceChild(link, targetElement);
          })
      }
    }
  }

  loadNativeContentBlock(config: Array<Record<string, any>>, type: Type<any>): void {
    config.forEach(c => {
      const container = new InContentBlockShellComponent(
        document.getElementById(c.targetElementID),
        this.appRef,
        this.componentFactoryResolver,
        this.injector
      );

      const componentRef = container.attach(type);

      if (type === MatchListBlockComponent || type === PreMatchPredictionsBlockComponent
        || type === MatchPageHeaderBlockComponent || type === MatchBestBetBlockComponent) {
        componentRef.instance.configurationObject = c;
      }

      if (type === FuturesMiniBlockComponent) {
        componentRef.instance.sportCode = c.sportCode;
      }

      if (type === DisplayAdComponent) {
        componentRef.instance.type = c.adID;
        // componentRef.instance.smallOnly = "true";
        // componentRef.instance.class = "small-only";
        // componentRef.instance.sportCode = c.sportCode;
        componentRef.instance.permanent = false;
      }

      this.inContentBlocks.push(container);
      
    })
  }

  loadWorldCupPreviewBlock(config: Array<Record<string, any>>): void {
    config.forEach(c => {
      const container = new InContentBlockShellComponent(
        document.getElementById(c.targetElementID),
        this.appRef,
        this.componentFactoryResolver,
        this.injector
      );

      container.attach(SwcPreviewBannerComponent);

      this.inContentBlocks.push(container);
      
    })
  }

  loadPolls(config) {
    // for (let i = 0; i < config.length; i++) {
    //   this.blogService.getSportsBettingPoll(config[i].pollId)
    //     .subscribe(res => {
    //       const ComponentCtor = Vue.extend(SBPoll)
    //       const componentInstance = new ComponentCtor({
    //         parent: this,
    //         propsData: {
    //           pollId: config[i].pollId,
    //           articleId: this.articleID,
    //         },
    //       })
    //       componentInstance.$mount(`#${config[i].targetElementID}`)
    //     })
    // }
  }
  
  loadContentBanner(config): void {
    if (typeof document !== "undefined") {
      config.forEach(c => {
        this.blogService.getResponsiveBanner(c.bannerId)
          .subscribe(promo => {
            // TODO
            console.log("Populating Banner:");
            console.log(promo);
            console.log("info config:");
            console.log(c);

            const container = new InContentBlockShellComponent(
              document.getElementById(c.targetElementID),
              this.appRef,
              this.componentFactoryResolver,
              this.injector
            );

            const componentRef = container.attach(ContentBannerComponent);

            componentRef.instance.promo = promo;

            this.inContentBlocks.push(container);
          })
      })

    }
  }


  // config options:
  // match ID
  // bet type (defaults to h2h)
  // bet side ('home', 'away', 'over' or 'under'; defaults to 'home' unless bet type is set to 'total', in which case it defaults to 'over')
  // bookmaker
  // stake (defaults to 20)
  // URL for image to show in banner on desktop
  // URL for image to show in banner on mobile
  // Alt text for image to show in banner on desktop (optional)
  // Alt text for image to show in banner on mobile (optional)

  // override bet string (optional)
  // override odds (optional)


  loadBettingActionBanner(config: Array<{
    matchID?: string;
    market?: string;
    bet?: string;
    bookmaker: string;
    stake?: number;
    overrideBetString?: string;
    overrideOdds?: number;
    desktopImageURL: string;
    desktopImageAltText: string;
    mobileImageURL: string;
    mobileImageAltText: string;
    targetURL?: string;
    targetElementID: string;
    buttonBackgroundColour?: string;
    buttonTextColour?: string;
    oddsTextColour?: string;
    destination?: string;
    overrideButtonText?: string;
    hideOdds?: boolean;
  }>): void {

    config.forEach(c => {


      let payout: number;
      let betString: string;
      let oddsString: string;
      let desktopButtonString: string;
      let mobileButtonString: string;
      let clickProps: string;

      if (!c.stake) {
        c.stake = 20;
      }
  
      // if both override bet string and override odds are provided, matchID is not required
      // otherwise, matchID, bookmakers, desktopImageURL, desktopImageAltText, mobileImageURL and mobileImageAltText are all required
      if (c.overrideBetString && c.overrideOdds) {
  
        if (!c.bookmaker) {
          console.error(`Betting Action Banner error on element #${c.targetElementID}: bookmaker not provided`);
        } else if (!c.desktopImageURL) {
          console.error(`Betting Action Banner error on element #${c.targetElementID}: desktop image URL not provided`)
        } else if (!c.desktopImageAltText) {
          console.error(`Betting Action Banner error on element #${c.targetElementID}: desktop image alt text not provided`)
        } else if (!c.mobileImageURL) {
          console.error(`Betting Action Banner error on element #${c.targetElementID}: mobile image URL not provided`)
        } else if (!c.mobileImageAltText) {
          console.error(`Betting Action Banner error on element #${c.targetElementID}: mobile image alt text not provided`)
        } else {

          // catch for URLs on old CDN: change https://statsinsider-wwwbeta.azureedge.net/static/* to https://cdn.ciphersports.io/*
          c.desktopImageURL = c.desktopImageURL.replace("https://statsinsider-wwwbeta.azureedge.net/static/", "https://cdn.ciphersports.io/");
          c.mobileImageURL = c.mobileImageURL.replace("https://statsinsider-wwwbeta.azureedge.net/static/", "https://cdn.ciphersports.io/");

          payout = c.stake * c.overrideOdds;
          betString = c.overrideBetString;
          oddsString = c.hideOdds ? "" : (c.overrideOdds === 1 ? "" : this.localisationService.convertDecimalOdds(c.overrideOdds));
          if (c.overrideButtonText) {
            desktopButtonString = c.overrideButtonText;
            mobileButtonString = c.overrideButtonText;
          } else {
            desktopButtonString = `Bet $${Number.isInteger(c.stake) ? c.stake : c.stake.toFixed(2)}<br/>Payout $${Number.isInteger(payout) ? payout : payout.toFixed(2)}`;
          mobileButtonString = `Bet $${Number.isInteger(c.stake) ? c.stake : c.stake.toFixed(2)}, Payout $${Number.isInteger(payout) ? payout : payout.toFixed(2)}`;
          }
          clickProps = JSON.stringify({
            "odds-bookmaker": c.bookmaker,
            "odds-path": document?.location.pathname,
            "odds-component": "betting-action-banner",
            "odds-type": "override",
            "odds-value": c.overrideOdds
          });

          this.populateBettingActionBanner(c, betString, oddsString, clickProps, desktopButtonString, mobileButtonString);
        }
      } else {
        if (!c.bookmaker) {
          console.error(`Betting Action Banner error on element #${c.targetElementID}: bookmaker not provided`);
        } else if (!c.matchID) {
          console.error(`Betting Action Banner error on element #${c.targetElementID}: neither match ID nor override parameter pair provided`)
        } else if (!c.desktopImageURL) {
          console.error(`Betting Action Banner error on element #${c.targetElementID}: desktop image URL not provided`)
        } else if (!c.desktopImageAltText) {
          console.error(`Betting Action Banner error on element #${c.targetElementID}: desktop image alt text not provided`)
        } else if (!c.mobileImageURL) {
          console.error(`Betting Action Banner error on element #${c.targetElementID}: mobile image URL not provided`)
        } else if (!c.mobileImageAltText) {
          console.error(`Betting Action Banner error on element #${c.targetElementID}: mobile image alt text not provided`)
        } else {

          // catch for URLs on old CDN: change https://statsinsider-wwwbeta.azureedge.net/static/* to https://cdn.ciphersports.io/*
          c.desktopImageURL = c.desktopImageURL.replace("https://statsinsider-wwwbeta.azureedge.net/static/", "https://cdn.ciphersports.io/");
          c.mobileImageURL = c.mobileImageURL.replace("https://statsinsider-wwwbeta.azureedge.net/static/", "https://cdn.ciphersports.io/");
          
          if (!c.market) {
            c.market = "h2h";
          }

          if (!["h2h", "line", "total"].includes(c.market)) {
            console.warn(`Betting Action Banner warning on element #${c.targetElementID}: market type ${c.market} is not valid. Defaulting to h2h`);
            c.market = "h2h";
          }

          if (!c.bet) {
            if (c.market === "total") {
              c.bet = "over";
            } else {
              c.bet = "home";
            }
          }

          if (c.market === "total") {
            if (!["over", "under"].includes(c.bet)) {
              console.warn(`Betting Action Banner warning on element #${c.targetElementID}: bet type ${c.bet} is not valid for market type ${c.market}. Defaulting to over`);
              c.bet = "over";
            }
          } else {
            if (!["home", "away"].includes(c.bet)) {
              console.warn(`Betting Action Banner warning on element #${c.targetElementID}: bet type ${c.bet} is not valid for market type ${c.market}. Defaulting to home`);
              c.bet = "home";
            }
          }

          this.sportDataService.getMatchData(c.matchID, true, [c.bookmaker])
            .subscribe(match => {
              if (!match || !match.aggregatedBettingInfo) {
                console.error(`Betting Action Banner error on element #${c.targetElementID}: betting data not found for bookmaker ${c.bookmaker} on match ${c.matchID}`);
              } else {
                let targetOdds;
                if (c.market === "h2h") {
                  targetOdds = c.bet === "home" ? match.aggregatedBettingInfo.HomeOdds : match.aggregatedBettingInfo.AwayOdds;
                } else if (c.market === "line") {
                  targetOdds = c.bet === "home" ? match.aggregatedBettingInfo.HomeLineOdds : match.aggregatedBettingInfo.AwayLineOdds;
                } else {
                  targetOdds = c.bet === "over" ? match.aggregatedBettingInfo.OverOdds : match.aggregatedBettingInfo.UnderOdds;
                }

                if (!targetOdds) {
                  console.error(`Betting Action Banner error on element #${c.targetElementID}: ${c.market} ${c.bet} odds not found for ${c.bookmaker} on match ${c.matchID}`);
                } else if ((c.market === "total" && !match.aggregatedBettingInfo.TotalLine)) {
                  console.error(`Betting Action Banner error on element #${c.targetElementID}: total mark not found for ${c.bookmaker} on match ${c.matchID}`);
                } else {
                  payout = targetOdds * c.stake;
                  oddsString = c.hideOdds ? "" : (targetOdds === 1 ? "" : this.localisationService.convertDecimalOdds(targetOdds));
                  desktopButtonString = `Bet $${Number.isInteger(c.stake) ? c.stake : c.stake.toFixed(2)}<br/>Payout $${Number.isInteger(payout) ? payout : payout.toFixed(2)}`;
                  mobileButtonString = `Bet $${Number.isInteger(c.stake) ? c.stake : c.stake.toFixed(2)}, Payout $${Number.isInteger(payout) ? payout : payout.toFixed(2)}`;

                  if (c.market === "h2h") {
                    betString = `${(c.bet === "home" ? match.MatchData.HomeTeam : match.MatchData.AwayTeam).DisplayName} ML vs. ${
                      (c.bet === "home" ? match.MatchData.AwayTeam : match.MatchData.HomeTeam).DisplayName}`;
                  } else if (c.market === "line") {
                    if (c.bet === "home") {
                      betString = `${match.MatchData.HomeTeam.DisplayName} (${
                        match.aggregatedBettingInfo.HomeLine > 0 ? '+' : ''}${match.aggregatedBettingInfo.HomeLine}) vs. ${
                        match.MatchData.AwayTeam.DisplayName}`;
                    } else {
                      betString = `${match.MatchData.AwayTeam.DisplayName} (${
                        match.aggregatedBettingInfo.HomeLine < 0 ? '+' : ''}${match.aggregatedBettingInfo.HomeLine * -1}) vs. ${
                        match.MatchData.HomeTeam.DisplayName}`;
                    }
                  } else {
                    if (environment.matchFirstTeam === "away") {
                      betString = `${match.MatchData.AwayTeam.DisplayName}-${match.MatchData.HomeTeam.DisplayName} ${
                        c.bet === "over" ? "Over" : "Under"} ${match.aggregatedBettingInfo.TotalLine}`;
                    } else {
                      betString = `${match.MatchData.HomeTeam.DisplayName}-${match.MatchData.AwayTeam.DisplayName} ${
                        c.bet === "over" ? "Over" : "Under"} ${match.aggregatedBettingInfo.TotalLine}`;
                    }
                    
                  }

                  clickProps = JSON.stringify({
                    "odds-bookmaker": c.bookmaker,
                    "odds-match": match.MatchData.SIMatchID,
                    "odds-path": document?.location.pathname,
                    "odds-component": "betting-action-banner",
                    "odds-sport": match.MatchData.Sport,
                    "odds-type": c.market,
                    "odds-option": c.bet === "away"
                      ? (match.MatchData.Sport === "TEN" ? match.MatchData.PlayerData.player2.nameDetails.first + " " + match.MatchData.PlayerData.player2.nameDetails.last : match.MatchData.AwayTeam.Abv)
                      : (c.bet === "home" ? (match.MatchData.Sport === "TEN" ? match.MatchData.PlayerData.player1.nameDetails.first + " " + match.MatchData.PlayerData.player1.nameDetails.last : match.MatchData.HomeTeam.Abv) : c.bet),
                    "odds-value": targetOdds
                  });

                  this.populateBettingActionBanner(c, betString, oddsString, clickProps, desktopButtonString, mobileButtonString);
                }
              }
            }, (err) => {
              console.error(`Betting Action Banner error on element #${c.targetElementID}: Failed to retrieve match data for match ${c.matchID}:`);
              console.error(err);
            })

        }
      }
    })
  }

  populateBettingActionBanner(config: Record<string, any>, betString: string, oddsString: string, clickProps: string, desktopButtonString: string, mobileButtonString: string): void {

    if (!config.destination) {
      config.destination = this.betService.customBookmakerLinks[config.bookmaker] || "";
    }
    
    const container = new InContentBlockShellComponent(
      document.getElementById(config.targetElementID),
      this.appRef,
      this.componentFactoryResolver,
      this.injector
    );

    const componentRef = container.attach(BettingActionBannerComponent);

    componentRef.instance.betString = this.sanitiser.bypassSecurityTrustHtml(betString);
    componentRef.instance.oddsString = this.sanitiser.bypassSecurityTrustHtml(oddsString);
    componentRef.instance.oddsTextColour = config.oddsTextColour;
    componentRef.instance.destination = config.destination;
    componentRef.instance.buttonBackgroundColour = config.buttonBackgroundColour;
    componentRef.instance.buttonTextColour = config.buttonTextColour;
    componentRef.instance.desktopButtonString = this.sanitiser.bypassSecurityTrustHtml(desktopButtonString);
    componentRef.instance.mobileButtonString = this.sanitiser.bypassSecurityTrustHtml(mobileButtonString);
    componentRef.instance.desktopImageURL = config.desktopImageURL;
    componentRef.instance.desktopImageAltText = config.desktopImageAltText;
    componentRef.instance.mobileImageURL = config.mobileImageURL;
    componentRef.instance.mobileImageAltText = config.mobileImageAltText;
    componentRef.instance.clickProps = clickProps;

    this.inContentBlocks.push(container);
  }

  ngOnDestroy(): void {
    // hack to clear out old pre-match blocks
    if (typeof document !== "undefined") {
      const blockContainers = document.querySelectorAll("div[id^='si-external-pre-match-']");
      Array.from(blockContainers).forEach(e => e.remove());

      this.inContentBlocks.forEach(c => c.dispose());
    }
  }

  excludeBookies(bookie: string): boolean {
    bookie = bookie.replace(/\s/g, '').toLowerCase()
    switch (bookie) {
      case 'bet365':
      case 'bet_365':
        return true
      case 'pointsbet':
        return true
      case 'betmgm':
        return true
      default:
        return false
    }
  }
}
